import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Line, ComposedChart } from "recharts";
import { Waves } from "lucide-react";
import api, { TRY } from "@/lib/api";
import { Card } from "@/components/Cards";

const fmtK = (v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}b` : v);

export default function Cashflow() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { api.get("/reports/cashflow").then((r) => setData(r.data)).catch((e) => setError(e.response?.data?.detail || e.message)); }, []);
  if (error) return <div className="h-64 flex items-center justify-center text-muted-foreground" data-testid="cashflow-error">Nakit akışı yüklenemedi: {String(error)}</div>;
  if (!data) return <div className="h-64 flex items-center justify-center text-muted-foreground">Yükleniyor…</div>;
  const chart = data.rows.map((r) => ({ ...r, supplierNeg: -r.supplier, staffNeg: -r.staff }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Nakit Akışı Takvimi</h1>
        <p className="text-sm text-muted-foreground mt-1">Önümüzdeki 8 hafta: beklenen tahsilatlar, tedarikçi ödemeleri ve personel borcu — vade bazlı.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Mini label="Beklenen Tahsilat" value={TRY(data.totals.receivable)} color="text-emerald-400" testId="cf-total-receivable" />
        <Mini label="Tedarikçi Ödemeleri" value={TRY(data.totals.supplier)} color="text-amber-400" testId="cf-total-supplier" />
        <Mini label="Personel Borcu" value={TRY(data.totals.staff)} color="text-rose-400" testId="cf-total-staff" />
        <Mini label="Net Nakit Pozisyonu" value={TRY(data.totals.net)} color={data.totals.net >= 0 ? "text-primary" : "text-rose-400"} testId="cf-total-net" />
      </div>

      <Card className="p-5">
        <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Waves className="h-5 w-5 text-primary" /> Haftalık Giriş / Çıkış</h3>
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chart} stackOffset="sign" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={fmtK} />
            <Tooltip formatter={(v, n) => [TRY(Math.abs(v)), n]} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="receivable" name="Tahsilat" stackId="a" fill="#10B981" />
            <Bar dataKey="supplierNeg" name="Tedarikçi" stackId="a" fill="#F59E0B" />
            <Bar dataKey="staffNeg" name="Personel" stackId="a" fill="#F43F5E" />
            <Line type="monotone" dataKey="cumulative" name="Kümülatif Net" stroke="#E27D38" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cashflow-table">
            <thead><tr className="border-b border-border bg-muted/40 text-left">
              {["Hafta", "Tahsilat (+)", "Tedarikçi (−)", "Personel (−)", "Net", "Kümülatif"].map((h, i) => (
                <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i > 0 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.label} data-testid={`cashflow-row-${r.overdue ? "overdue" : r.later ? "later" : r.start}`}
                  className={`border-b border-border/60 ${r.overdue ? "bg-rose-500/5" : r.later ? "bg-muted/20" : ""}`}>
                  <td className="px-4 py-3 font-medium whitespace-nowrap">
                    {r.overdue ? <span className="text-rose-400">⚠ Gecikmiş</span> : r.label}
                    {r.start && <span className="ml-2 text-[10px] text-muted-foreground font-mono">{r.start}</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-400">{r.receivable ? TRY(r.receivable) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-400">{r.supplier ? TRY(r.supplier) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-400">{r.staff ? TRY(r.staff) : "—"}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${r.net >= 0 ? "" : "text-rose-400"}`}>{TRY(r.net)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${r.cumulative >= 0 ? "text-primary" : "text-rose-400"}`}>{TRY(r.cumulative)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-muted-foreground">Tahsilat ve tedarikçi kalemleri açık bakiyeyle sınırlandırılır (kısmi ödemeler en yeni vadeden düşülür). Personel borcu içinde bulunulan ayın son haftasına yerleştirilir.</p>
    </div>
  );
}

const Mini = ({ label, value, color, testId }) => (
  <Card className="p-4" data-testid={testId}>
    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`mt-2 font-mono text-lg sm:text-xl font-bold ${color}`}>{value}</div>
  </Card>
);
